import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { InteractionManager, TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { FormSelectInput, showConfirm, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  parseLevelNumber,
  sortProgressItemBadges,
} from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import type { PlayerProfileDoc, PlayerProgressItem } from '@/features/player/components/types';
import { playerKeys } from '@/features/player/queries/query-keys';
import { createStyles, useScaling } from '@/hooks';
import { updatePlayerCollection } from '@/libs/firebase/firestore/firestore-helpers';
import { useUiStore } from '@/stores/ui-store';
import { colors } from '@/theme/colors';
import { useQueryClient } from '@tanstack/react-query';

import { useOverviewSessionUiStore } from '@/features/player-session/stores/overview-session-ui-store';
import { patchSessionListPlayerAfterProgressUpdate } from '@/features/player-session/utils';
import { useShallow } from 'zustand/shallow';
import { LEVEL_OPTIONS } from './constants';
import { LevelRow } from './level-row';
import {
  badgeAggregateStatusFromCheckpoints,
  checkpointDisplayStatus,
  levelAggregateStatusFromBadges,
} from './mutate-level-status';
import type { MutatePlayerForm } from './types';

function hasCompletedDate(item: PlayerProgressItem | undefined): boolean {
  return Boolean(String(item?.dateCompleted ?? '').trim());
}

export function MutatePlayerLevelTab({
  playerId,
  playerDisplayName,
  progressItems,
  initialProfileLevelValue,
}: {
  playerId: string;
  playerDisplayName: string;
  progressItems: PlayerProgressItem[];
  /** e.g. "level_8" from Firestore/profile; avoids locking to defaultValues (level_1). */
  initialProfileLevelValue?: string | null;
}) {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const requestOverviewSessionRefresh = useOverviewSessionUiStore(
    useShallow(s => s.requestRefresh),
  );
  const showGlobalLoading = useUiStore(s => s.showGlobalLoading);
  const hideGlobalLoading = useUiStore(s => s.hideGlobalLoading);
  const form = useFormContext<MutatePlayerForm>();
  const [levelEditMode, setLevelEditMode] = useState(false);
  // Spec: allow opening multiple levels at once
  const [expandedLevels, setExpandedLevels] = useState<Set<number>>(() => new Set());
  const [expandedBadges, setExpandedBadges] = useState<Set<string>>(() => new Set());

  // "CURRENT" in the list should reflect the player's current level from profile data
  // at the time the screen is opened, not the dropdown selection.
  /**
   * IMPORTANT: this tab can mount before the form is hydrated with profile data
   * (because we keep tabs mounted to preserve state). So we "lock in" the
   * initial profile level only once, when the form has a non-empty value.
   */
  const profileLevelValueRef = useRef<string | null | undefined>(undefined);
  const profileLevelLockedRef = useRef(false);
  const [profileLevelValue, setProfileLevelValue] = useState<string | null | undefined>(undefined);

  const selectedLevelValue = form.watch('level');
  useEffect(() => {
    if (profileLevelLockedRef.current) return;

    const fromProfile = String(initialProfileLevelValue ?? '').trim();
    if (fromProfile) {
      profileLevelLockedRef.current = true;
      profileLevelValueRef.current = fromProfile;
      setProfileLevelValue(fromProfile);
      return;
    }

    // Fallback: when profile value wasn't provided, lock once the form is hydrated with a non-empty value.
    const v = String(selectedLevelValue ?? '').trim();
    if (!v) return;
    profileLevelLockedRef.current = true;
    profileLevelValueRef.current = v;
    setProfileLevelValue(v);
  }, [initialProfileLevelValue, selectedLevelValue]);

  const profileCurrentLevelNumber = useMemo(() => {
    const n = Number(String(profileLevelValue ?? '').replace('level_', ''));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }, [profileLevelValue]);

  // iOS parity: if the level dropdown was changed, require saving before allowing progress deletes.
  const hasLockedProfileLevel = Boolean(String(profileLevelValue ?? '').trim());
  const levelChanged =
    hasLockedProfileLevel &&
    String(selectedLevelValue ?? '').trim() !== String(profileLevelValue ?? '').trim();

  useEffect(() => {
    if (!levelChanged) return;
    setLevelEditMode(false);
    setCheckedLevels(new Set());
    setCheckedBadges(new Set());
    setCheckedCheckpoints(new Set());
  }, [levelChanged]);

  const progressByLevel = useMemo(() => {
    const m = new Map<number, PlayerProgressItem>();
    for (const p of progressItems) {
      const n = parseLevelNumber(p?.name);
      if (n != null && n >= 1) m.set(n, sortProgressItemBadges(p));
    }
    return m;
  }, [progressItems]);

  const levelColorByNumber = useMemo(() => {
    return (n: number) => {
      const key = `Level ${n}` as keyof typeof colors;
      return colors[key] ?? colors.Blue;
    };
  }, []);

  const buildBadgeKey = (levelLabel: string, badgeName: string, badgeIdx: number) =>
    `${levelLabel}-${badgeName}-${badgeIdx}`;
  /** Name-based so it matches UI rows regardless of `sortProgressCheckpoints` order vs raw indices. */
  const buildCheckpointKey = (badgeKey: string, checkpointName: string) =>
    `${badgeKey}-${String(checkpointName ?? '').trim()}`;

  /**
   * Levels that may show a checkbox (iOS parity):
   * - Completed OR Partial levels are selectable
   * - Incomplete levels are not
   */
  const eligibleCheckLevels = useMemo(() => {
    const s = new Set<number>();
    for (const p of progressItems) {
      const n = parseLevelNumber(p?.name);
      if (n == null || n < 1) continue;
      const agg = levelAggregateStatusFromBadges(p);
      const hasAnyCompletedCheckpoint = (p?.badges ?? []).some(b =>
        (b.checkpoints ?? []).some(cp => checkpointDisplayStatus(cp.status) === 'completed'),
      );
      if (
        agg === 'completed' ||
        agg === 'partial' ||
        hasCompletedDate(p) ||
        p?.completed === true ||
        // Spec update: allow edit if ANY checkpoint is completed (even when all 10 levels are otherwise incomplete).
        hasAnyCompletedCheckpoint
      ) {
        s.add(n);
      }
    }
    return s;
  }, [progressItems]);

  const [checkedLevels, setCheckedLevels] = useState<Set<number>>(() => new Set());
  const [checkedBadges, setCheckedBadges] = useState<Set<string>>(() => new Set());
  const [checkedCheckpoints, setCheckedCheckpoints] = useState<Set<string>>(() => new Set());

  const checkedCount = checkedLevels.size + checkedBadges.size + checkedCheckpoints.size;

  const toggleLevelChecked = (n: number) => {
    if (!levelEditMode) return;
    if (!eligibleCheckLevels.has(n)) return;

    const levelLabel = `LEVEL ${n}`;
    const item = progressByLevel.get(n);
    const badges = item?.badges ?? [];
    const wasChecked = checkedLevels.has(n);
    const nextChecked = !wasChecked;

    setCheckedLevels(prev => {
      const next = new Set(prev);
      if (nextChecked) next.add(n);
      else next.delete(n);
      return next;
    });

    // Select/unselect only badges & checkpoints that show a checkbox (not incomplete).
    setCheckedBadges(prev => {
      const next = new Set(prev);
      for (let badgeIdx = 0; badgeIdx < badges.length; badgeIdx++) {
        const b = badges[badgeIdx];
        const badgeKey = buildBadgeKey(levelLabel, b.name, badgeIdx);
        if (badgeAggregateStatusFromCheckpoints(b) === 'incomplete') {
          if (!nextChecked) next.delete(badgeKey);
          continue;
        }
        if (nextChecked) next.add(badgeKey);
        else next.delete(badgeKey);
      }
      return next;
    });

    setCheckedCheckpoints(prev => {
      const next = new Set(prev);
      for (let badgeIdx = 0; badgeIdx < badges.length; badgeIdx++) {
        const b = badges[badgeIdx];
        const badgeKey = buildBadgeKey(levelLabel, b.name, badgeIdx);
        const cps = b.checkpoints ?? [];
        for (let cpIdx = 0; cpIdx < cps.length; cpIdx++) {
          const cp = cps[cpIdx];
          const cpKey = buildCheckpointKey(badgeKey, cp.name);
          if (checkpointDisplayStatus(cp.status) !== 'completed') {
            if (!nextChecked) next.delete(cpKey);
            continue;
          }
          if (nextChecked) next.add(cpKey);
          else next.delete(cpKey);
        }
      }
      return next;
    });
  };

  const toggleBadgeChecked = (levelLabel: string, item: PlayerProgressItem | undefined) => {
    return (badgeKey: string, nextChecked: boolean) => {
      setCheckedBadges(prev => {
        const next = new Set(prev);
        if (nextChecked) next.add(badgeKey);
        else next.delete(badgeKey);
        return next;
      });

      // Selecting a badge selects all checkpoints under it (and deselects when unchecking).
      setCheckedCheckpoints(prev => {
        const next = new Set(prev);
        const badges = item?.badges ?? [];
        for (let badgeIdx = 0; badgeIdx < badges.length; badgeIdx++) {
          const b = badges[badgeIdx];
          const key = buildBadgeKey(levelLabel, b.name, badgeIdx);
          if (key !== badgeKey) continue;
          const cps = b.checkpoints ?? [];
          for (let cpIdx = 0; cpIdx < cps.length; cpIdx++) {
            const cp = cps[cpIdx];
            if (checkpointDisplayStatus(cp.status) !== 'completed') {
              if (!nextChecked) next.delete(buildCheckpointKey(badgeKey, cp.name));
              continue;
            }
            const cpKey = buildCheckpointKey(badgeKey, cp.name);
            if (nextChecked) next.add(cpKey);
            else next.delete(cpKey);
          }
        }
        return next;
      });

      // If all selectable badges under this level are selected, auto-select the level checkbox.
      const levelNum = Number(String(levelLabel).replace('LEVEL ', ''));
      if (!Number.isFinite(levelNum)) return;
      const badges = item?.badges ?? [];
      const allBadgesSelected = badges.every((b, badgeIdx) => {
        const key = buildBadgeKey(levelLabel, b.name, badgeIdx);
        if (badgeAggregateStatusFromCheckpoints(b) === 'incomplete') return true;
        if (key === badgeKey) return nextChecked;
        return checkedBadges.has(key);
      });
      setCheckedLevels(prev => {
        const next = new Set(prev);
        if (allBadgesSelected) next.add(levelNum);
        else next.delete(levelNum);
        return next;
      });
    };
  };

  const toggleCheckpointChecked = (levelLabel: string, item: PlayerProgressItem | undefined) => {
    return (checkpointKey: string, nextChecked: boolean) => {
      // Update checkpoints then derive badge/level selection from the updated set.
      setCheckedCheckpoints(prev => {
        const next = new Set(prev);
        if (nextChecked) next.add(checkpointKey);
        else next.delete(checkpointKey);

        const badges = item?.badges ?? [];
        for (let badgeIdx = 0; badgeIdx < badges.length; badgeIdx++) {
          const b = badges[badgeIdx];
          const badgeKey = buildBadgeKey(levelLabel, b.name, badgeIdx);
          const cps = b.checkpoints ?? [];
          if (cps.length === 0) continue;

          const selectableCps = cps.filter(
            cp => checkpointDisplayStatus(cp.status) === 'completed',
          );
          const allSelected =
            selectableCps.length > 0 &&
            selectableCps.every(cp => next.has(buildCheckpointKey(badgeKey, cp.name)));

          setCheckedBadges(prevBadges => {
            const nextBadges = new Set(prevBadges);
            if (badgeAggregateStatusFromCheckpoints(b) === 'incomplete') {
              nextBadges.delete(badgeKey);
            } else if (allSelected) nextBadges.add(badgeKey);
            else nextBadges.delete(badgeKey);
            return nextBadges;
          });
        }

        // After badges settle, also update the level checkbox based on all selectable badges selected.
        const levelNum = Number(String(levelLabel).replace('LEVEL ', ''));
        if (Number.isFinite(levelNum)) {
          const allBadgesSelected = badges.every((b, badgeIdx) => {
            const badgeKey = buildBadgeKey(levelLabel, b.name, badgeIdx);
            if (badgeAggregateStatusFromCheckpoints(b) === 'incomplete') return true;
            const cps = b.checkpoints ?? [];
            const selectableCps = cps.filter(
              cp => checkpointDisplayStatus(cp.status) === 'completed',
            );
            if (selectableCps.length === 0) return checkedBadges.has(badgeKey);
            return selectableCps.every(cp => next.has(buildCheckpointKey(badgeKey, cp.name)));
          });
          setCheckedLevels(prevLevels => {
            const nextLevels = new Set(prevLevels);
            if (allBadgesSelected) nextLevels.add(levelNum);
            else nextLevels.delete(levelNum);
            return nextLevels;
          });
        }

        return next;
      });
    };
  };

  const handleClearDataPress = () => {
    if (checkedCount === 0 || !playerId.trim()) {
      return;
    }
    if (levelChanged) {
      return;
    }

    const run = async () => {
      const ok = await showConfirm({
        title: STRINGS.player.mutatePlayer.levelTab.removeProgressTitle,
        message: STRINGS.player.mutatePlayer.levelTab.removeProgressMessage(playerDisplayName),
        cancelText: STRINGS.player.mutatePlayer.levelTab.removeProgressNo,
        confirmText: STRINGS.player.mutatePlayer.levelTab.removeProgressYes,
        confirmStyle: 'destructive',
      });
      if (!ok) {
        return;
      }

      showGlobalLoading();
      // Let the confirm dialog finish painting away so the global overlay is visible (iOS Alert).
      await new Promise<void>(resolve => {
        InteractionManager.runAfterInteractions(() => resolve());
      });

      const toRemoveLevels = new Set(checkedLevels);
      const toRemoveBadges = new Set(checkedBadges);
      const toRemoveCheckpoints = new Set(checkedCheckpoints);
      const cached = queryClient.getQueryData(playerKeys.profile(playerId)) as
        | PlayerProfileDoc
        | null
        | undefined;
      const progress = cached != null ? (cached.progress ?? progressItems) : progressItems;
      const nextProgress = progress.map(item => {
        const levelNum = parseLevelNumber(item?.name);
        const levelLabel = `LEVEL ${levelNum ?? ''}`.trim();
        const removeByLevel = levelNum != null && toRemoveLevels.has(levelNum);

        const nextBadges = (item.badges ?? []).map((badge, badgeIdx) => {
          const badgeKey = `${levelLabel}-${badge.name}-${badgeIdx}`;
          const removeBadge = removeByLevel || toRemoveBadges.has(badgeKey);
          const checkpoints = badge.checkpoints ?? [];

          const nextCheckpoints = checkpoints.map(cp => {
            const checkpointKey = buildCheckpointKey(badgeKey, cp.name);
            const removeCheckpoint = removeBadge || toRemoveCheckpoints.has(checkpointKey);
            if (!removeCheckpoint) return cp;
            return { ...cp, status: 'unattempted', attempts: [] as unknown[] };
          });

          const clearedAnyCheckpointHere = checkpoints.some(cp =>
            toRemoveCheckpoints.has(buildCheckpointKey(badgeKey, cp.name)),
          );

          let nextBadgeEarned = badge.badgeEarned;
          let nextDateEarned = badge.dateEarned;
          if (removeBadge) {
            nextBadgeEarned = false;
            nextDateEarned = undefined;
          } else if (clearedAnyCheckpointHere) {
            // Spec: clearing a checkpoint should only unset `badgeEarned` for this badge.
            // Do not modify `dateEarned` unless the badge itself is cleared.
            nextBadgeEarned = false;
          }

          return {
            ...badge,
            badgeEarned: nextBadgeEarned,
            dateEarned: nextDateEarned,
            checkpoints: nextCheckpoints,
          };
        });

        return {
          ...item,
          // Spec: only clearing the whole level should reset completion fields.
          completed: removeByLevel ? false : item.completed,
          dateCompleted: removeByLevel ? undefined : item.dateCompleted,
          badges: nextBadges,
        };
      });

      const previous = cached;

      if (cached) {
        queryClient.setQueryData(playerKeys.profile(playerId), {
          ...cached,
          progress: nextProgress,
        } satisfies PlayerProfileDoc);
      }

      try {
        await updatePlayerCollection<PlayerProfileDoc>(playerId, { progress: nextProgress });
        if (!previous) {
          await queryClient.invalidateQueries({ queryKey: playerKeys.profile(playerId) });
        }
        patchSessionListPlayerAfterProgressUpdate(queryClient, {
          playerId,
          nextProgress: nextProgress as unknown as playerSession.Progress[],
        });
        requestOverviewSessionRefresh();
        setLevelEditMode(false);
        setCheckedLevels(new Set());
        setCheckedBadges(new Set());
        setCheckedCheckpoints(new Set());
      } catch {
        if (previous) {
          queryClient.setQueryData(playerKeys.profile(playerId), previous);
        }
      } finally {
        hideGlobalLoading();
      }
    };

    run();
  };

  return (
    <Stack width="100%" gap={scale(12)}>
      <Stack gap={scale(8)}>
        <Text
          fontSize={scale(10)}
          fontWeight="500"
          fontFamily="Montserrat"
          style={styles.selectLabel}>
          {String(STRINGS.player.mutatePlayer.levelTab.changeCurrentLevelTitle).toUpperCase()}
        </Text>
        <Stack width={scale({ phone: 160, tablet: 220 })}>
          <Controller
            name="level"
            control={form.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormSelectInput
                required={false}
                options={LEVEL_OPTIONS}
                value={value}
                onValueChange={level => onChange(level)}
                placeholder={STRINGS.player.mutatePlayer.profile.levelPlaceholder}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
              />
            )}
          />
        </Stack>
      </Stack>

      {levelChanged ? (
        <Text variant="Font Inter/Subtitle/Regular 12px" style={styles.levelChangedHint}>
          {STRINGS.player.mutatePlayer.levelTab.levelChangedHint}
        </Text>
      ) : (
        <>
          <Stack direction="row" align="center" justify="space-between">
            {checkedCount > 0 && levelEditMode ? (
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={handleClearDataPress}
                style={styles.clearDataRow}>
                <SvgIcon name="IcClear" size={scale(16)} color={colors.Error} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.clearDataText}>
                  {STRINGS.player.mutatePlayer.levelTab.clearData}
                </Text>
              </TouchableOpacity>
            ) : (
              <Stack />
            )}
            {eligibleCheckLevels.size > 0 ? (
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() =>
                  setLevelEditMode(prev => {
                    const next = !prev;
                    setCheckedLevels(new Set());
                    setCheckedBadges(new Set());
                    setCheckedCheckpoints(new Set());
                    return next;
                  })
                }
                style={styles.levelHeaderAction}>
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.levelHeaderActionText}>
                  {levelEditMode
                    ? STRINGS.player.mutatePlayer.levelTab.done
                    : STRINGS.player.mutatePlayer.levelTab.edit}
                </Text>
                <SvgIcon
                  name={levelEditMode ? 'IcCheck' : 'IcEdit'}
                  size={scale(24)}
                  color={colors.Blue}
                />
              </TouchableOpacity>
            ) : null}
          </Stack>

          {isTablet ? (
            <Stack direction="row" gap={scale(12)} width="100%">
              <Stack flex={1} gap={scale(10)}>
                {LEVEL_OPTIONS.filter((_, idx) => idx % 2 === 0).map(opt => {
                  const n = Number(String(opt.value).replace('level_', ''));
                  const isCurrent = n === profileCurrentLevelNumber;
                  const statusKey = levelAggregateStatusFromBadges(progressByLevel.get(n));
                  const canCheck = eligibleCheckLevels.has(n);
                  const checked = checkedLevels.has(n);
                  const levelLabel = `LEVEL ${n}`;
                  const item = progressByLevel.get(n);
                  return (
                    <LevelRow
                      key={opt.value}
                      label={levelLabel}
                      statusKey={statusKey}
                      levelColor={levelColorByNumber(n)}
                      current={isCurrent}
                      editMode={levelEditMode}
                      canCheck={canCheck}
                      checked={checked}
                      styles={styles}
                      editBgDisabled={levelEditMode && !canCheck}
                      expanded={expandedLevels.has(n)}
                      progressItem={item}
                      expandedBadges={expandedBadges}
                      selectedBadgeKeys={checkedBadges}
                      onToggleBadgeChecked={toggleBadgeChecked(levelLabel, item)}
                      selectedCheckpointKeys={checkedCheckpoints}
                      onToggleCheckpointChecked={toggleCheckpointChecked(levelLabel, item)}
                      onToggleBadgeExpanded={badgeKey => {
                        setExpandedBadges(prev => {
                          const next = new Set(prev);
                          if (next.has(badgeKey)) next.delete(badgeKey);
                          else next.add(badgeKey);
                          return next;
                        });
                      }}
                      onToggleExpanded={() => {
                        setExpandedLevels(prev => {
                          const next = new Set(prev);
                          if (next.has(n)) next.delete(n);
                          else next.add(n);
                          return next;
                        });
                      }}
                      onToggle={() => toggleLevelChecked(n)}
                      onPressCard={() => {
                        if (levelEditMode) return;
                        // Spec update: do not change level by tapping the card.
                      }}
                    />
                  );
                })}
              </Stack>
              <Stack flex={1} gap={scale(10)}>
                {LEVEL_OPTIONS.filter((_, idx) => idx % 2 === 1).map(opt => {
                  const n = Number(String(opt.value).replace('level_', ''));
                  const isCurrent = n === profileCurrentLevelNumber;
                  const statusKey = levelAggregateStatusFromBadges(progressByLevel.get(n));
                  const canCheck = eligibleCheckLevels.has(n);
                  const checked = checkedLevels.has(n);
                  const levelLabel = `LEVEL ${n}`;
                  const item = progressByLevel.get(n);
                  return (
                    <LevelRow
                      key={opt.value}
                      label={levelLabel}
                      statusKey={statusKey}
                      levelColor={levelColorByNumber(n)}
                      current={isCurrent}
                      editMode={levelEditMode}
                      canCheck={canCheck}
                      checked={checked}
                      styles={styles}
                      editBgDisabled={levelEditMode && !canCheck}
                      expanded={expandedLevels.has(n)}
                      progressItem={item}
                      expandedBadges={expandedBadges}
                      selectedBadgeKeys={checkedBadges}
                      onToggleBadgeChecked={toggleBadgeChecked(levelLabel, item)}
                      selectedCheckpointKeys={checkedCheckpoints}
                      onToggleCheckpointChecked={toggleCheckpointChecked(levelLabel, item)}
                      onToggleBadgeExpanded={badgeKey => {
                        setExpandedBadges(prev => {
                          const next = new Set(prev);
                          if (next.has(badgeKey)) next.delete(badgeKey);
                          else next.add(badgeKey);
                          return next;
                        });
                      }}
                      onToggleExpanded={() => {
                        setExpandedLevels(prev => {
                          const next = new Set(prev);
                          if (next.has(n)) next.delete(n);
                          else next.add(n);
                          return next;
                        });
                      }}
                      onToggle={() => toggleLevelChecked(n)}
                      onPressCard={() => {
                        if (levelEditMode) return;
                        // Spec update: do not change level by tapping the card.
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          ) : (
            <Stack gap={scale(10)}>
              {LEVEL_OPTIONS.map(opt => {
                const n = Number(String(opt.value).replace('level_', ''));
                const isCurrent = n === profileCurrentLevelNumber;
                const statusKey = levelAggregateStatusFromBadges(progressByLevel.get(n));
                const canCheck = eligibleCheckLevels.has(n);
                const checked = checkedLevels.has(n);
                const levelLabel = `LEVEL ${n}`;
                const item = progressByLevel.get(n);
                return (
                  <LevelRow
                    key={opt.value}
                    label={levelLabel}
                    statusKey={statusKey}
                    levelColor={levelColorByNumber(n)}
                    current={isCurrent}
                    editMode={levelEditMode}
                    canCheck={canCheck}
                    checked={checked}
                    styles={styles}
                    editBgDisabled={levelEditMode && !canCheck}
                    expanded={expandedLevels.has(n)}
                    progressItem={item}
                    expandedBadges={expandedBadges}
                    selectedBadgeKeys={checkedBadges}
                    onToggleBadgeChecked={toggleBadgeChecked(levelLabel, item)}
                    selectedCheckpointKeys={checkedCheckpoints}
                    onToggleCheckpointChecked={toggleCheckpointChecked(levelLabel, item)}
                    onToggleBadgeExpanded={badgeKey => {
                      setExpandedBadges(prev => {
                        const next = new Set(prev);
                        if (next.has(badgeKey)) next.delete(badgeKey);
                        else next.add(badgeKey);
                        return next;
                      });
                    }}
                    onToggleExpanded={() => {
                      setExpandedLevels(prev => {
                        const next = new Set(prev);
                        if (next.has(n)) next.delete(n);
                        else next.add(n);
                        return next;
                      });
                    }}
                    onToggle={() => toggleLevelChecked(n)}
                    onPressCard={() => {
                      if (levelEditMode) return;
                      // Spec update: do not change level by tapping the card.
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  selectLabel: {
    color: colors['10'],
  },
  changeLevelTitle: {
    color: colors['6'],
    letterSpacing: 0.4,
  },
  levelHeaderAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
  },
  levelChangedHint: {
    color: colors['6'],
    paddingVertical: scale(12),
  },
  levelHeaderActionText: {
    color: colors.Blue,
  },
  clearDataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
    alignSelf: 'flex-start',
    height: scale(24),
  },
  clearDataText: {
    color: colors.Red,
    textTransform: 'uppercase',
  },
  levelRow: {
    borderWidth: 1,
    borderColor: colors['3'],
    borderRadius: scale(8),
    paddingVertical: scale(16),
    paddingHorizontal: scale(20),
    width: '100%',
  },
  levelRowInner: {
    position: 'relative',
  },
  chevronButton: {
    paddingLeft: scale(8),
    paddingVertical: scale(4),
  },
  chevronIcon: {
    transform: [{ rotate: '90deg' }],
  },
  chevronIconExpanded: {
    transform: [{ rotate: '-90deg' }],
  },
  levelExpanded: {
    paddingTop: scale(6),
  },
  badgeBlock: {
    alignItems: 'center',
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
    paddingVertical: scale(10),
    borderBottomWidth: 1,
    borderBottomColor: colors['3'],
  },
  badgeRowExpanded: {
    // Design: when expanded, the skill header shouldn't add an extra divider above its steps.
    borderBottomWidth: 0,
  },
  badgeRowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(6),
    flex: 1,
    minWidth: 0,
  },
  badgeChevron: {
    transform: [{ rotate: '90deg' }],
  },
  badgeChevronExpanded: {
    transform: [{ rotate: '-90deg' }],
  },
  badgeTitle: {
    flex: 1,
  },
  checkpointsBlock: {
    paddingLeft: scale(24),
    paddingBottom: scale(6),
    width: '100%',
  },
  checkpointRow: {
    paddingVertical: scale(8),
  },
  checkpointRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors['3'],
  },
  checkpointTitle: {
    color: colors['6'],
    flex: 1,
  },
  checkboxFaded: {
    opacity: 0.7,
  },
  levelRowView: {
    backgroundColor: 'white',
  },
  levelRowEdit: {
    backgroundColor: 'white',
  },
  levelRowEditBgEnabled: {
    backgroundColor: 'white',
  },
  levelRowEditBgDisabled: {
    backgroundColor: '#F6F8FC',
  },
  levelRowCurrent: {
    borderColor: colors.Blue,
    borderWidth: 1.5,
  },
  levelTitle: {
    color: colors['Labels/Primary'],
  },
  levelRowStatusCompleted: {
    color: colors.Success,
  },
  levelRowStatusPartial: {
    color: '#FF9D00',
  },
  levelRowStatusIncomplete: {
    color: colors['6'],
  },
  statusPill: {
    paddingHorizontal: scale(10),
    paddingVertical: scale(4),
    borderRadius: scale(30),
  },
  statusPillText: {
    fontSize: scale(10),
  },
  pillCompleted: {
    backgroundColor: '#BAD3FF',
  },
  pillTextCompleted: {
    color: colors.Blue,
  },
  pillPartial: {
    backgroundColor: '#FFF2D1',
  },
  pillTextPartial: {
    color: '#FF9D00',
  },
  pillIncomplete: {
    backgroundColor: colors['2'],
  },
  pillTextIncomplete: {
    color: colors['10'],
  },
  currentBadge: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: colors.Blue,
    height: scale(16),
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomLeftRadius: scale(10),
    borderTopRightRadius: scale(6),
    paddingHorizontal: scale(8),
  },
  currentBadgeText: {
    color: 'white',
    fontSize: scale(8),
    fontWeight: '600',
    fontFamily: 'Inter',
  },
}));
